import React from "react";
import { Award, Target, Users, Zap } from "lucide-react";
import { COACH_LOGOS } from "../data/site";

const VALUES = [
    {
        icon: Award,
        title: "Qualità premium",
        text: "Servizio di livello superiore, incluso in un piano completo senza sovrapprezzi.",
    },
    {
        icon: Target,
        title: "Massima personalizzazione",
        text: "Ogni piano è costruito su di te: obiettivi, abitudini, struttura e stile di vita.",
    },
    {
        icon: Users,
        title: "Sempre presenti",
        text: "Supporto diretto con il coach 7 giorni su 7. Tu ti alleni, noi ci siamo.",
    },
    {
        icon: Zap,
        title: "Risultati concreti",
        text: "Programmazione aggressiva ma sostenibile per vedere progressi reali e duraturi.",
    },
];

export default function About() {
    return (
        <section
            id="chi-siamo"
            data-testid="about-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid opacity-40" />
            <div className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-gold/5 blur-3xl" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    {/* Left: founder logo */}
                    <div className="lg:col-span-5 relative">
                        <div className="relative aspect-square rounded-2xl overflow-hidden border border-gold/20 bg-ink">
                            <img
                                src={COACH_LOGOS.manuel}
                                alt="Manuel Romano - Founder RPF Coaching"
                                data-testid="about-founder-logo"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="font-oswald text-[11px] uppercase tracking-[0.3em] text-gold mb-1">
                                    Founder
                                </div>
                                <div className="font-oswald font-bold text-3xl text-white">
                                    Manuel Romano
                                </div>
                            </div>
                        </div>
                        <div className="absolute -z-10 -bottom-6 -right-6 w-40 h-40 border-2 border-gold/30 rounded-2xl hidden sm:block" />
                        <div className="absolute -z-10 -top-6 -left-6 w-32 h-32 bg-gold/10 rounded-2xl blur-2xl hidden sm:block" />
                    </div>

                    {/* Right: text */}
                    <div className="lg:col-span-7">
                        <div className="font-manrope text-sm uppercase tracking-[0.3em] text-gold mb-4">
                            Chi Siamo
                        </div>
                        <h2
                            data-testid="about-title"
                            className="font-oswald font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[1]"
                        >
                            Un team che{" "}
                            <span className="text-shimmer">prende seriamente</span>{" "}
                            il tuo obiettivo.
                        </h2>

                        <div className="mt-8 space-y-5 font-manrope text-zinc-300 text-base sm:text-lg leading-relaxed">
                            <p>
                                Sono <span className="text-white font-semibold">Manuel Romano</span>,
                                founder del team RPF. Faccio il coach ormai da anni e, nonostante
                                la giovane età, ho imparato a dosare le informazioni, capire
                                davvero come funziona questo mondo e costruire qualcosa di
                                concreto: un'attività vera, non più un hobby.
                            </p>
                            <p>
                                Allenarsi e allenare è ciò che so fare di meglio nella mia vita.
                                Rendere il servizio il più piacevole possibile per il cliente,
                                avere sempre qualcosa in più rispetto alla concorrenza — questa è
                                la mia filosofia lavorativa.
                            </p>
                            <p>
                                Offro un servizio tra i più avanzati e completi sul mercato:
                                ciò che altri chiamano <em className="text-gold not-italic">premium</em>{" "}
                                o <em className="text-gold not-italic">luxury</em> e fanno
                                pagare cifre folli, qui è semplicemente il minimo. Il mio lavoro è
                                seguire in modo professionale ogni singolo cliente. Punto.
                            </p>
                        </div>

                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            {VALUES.map((v) => {
                                const Icon = v.icon;
                                return (
                                    <div
                                        key={v.title}
                                        data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="group p-5 rounded-xl bg-ink-surface border border-white/5 hover:border-gold/40 transition-all"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                                                <Icon size={20} className="text-gold" />
                                            </div>
                                            <div>
                                                <div className="font-oswald font-bold uppercase tracking-wide text-white text-lg">
                                                    {v.title}
                                                </div>
                                                <div className="font-manrope text-sm text-zinc-400 mt-1">
                                                    {v.text}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
